import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    TextField,
    Typography,
    Box,
    Autocomplete,
    ToggleButton,
    ToggleButtonGroup,
    MenuItem,
    Checkbox,
    Button,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FieldGroup from '../FieldGroup';
import { DropzoneUpload } from 'routes/Pages/PurchaseRequisition/components/FormControls';
import { pillGroupSx, pillLabelSx } from './ContactStep';
import { useGetWhDetails } from 'services/globalHooks/useGetWhDetails';
import { useGetCustomers } from 'services/globalHooks/useGetCustomers';
import { useGetProducts } from 'services/globalHooks/useGetProducts';

const LOCATION_OPTIONS = ['Delivered', 'Roadside'];
const UOM_OPTIONS = ['Tonnes', 'M3'];
const QUALITY_OPTIONS = ['High', 'Standard', 'Low'];

const cardSx = {
    border: '1px solid #ece7df',
    borderRadius: '10px',
    p: 2.5,
    backgroundColor: '#fff',
};

const cellSx = { px: 0.75, py: 0.75, borderBottom: '1px solid #f0ebe3' };
const headCellSx = { ...cellSx, fontSize: '0.78rem', fontWeight: 700, color: '#6b6558', whiteSpace: 'nowrap' };

function SelectCell({ value, options, onChange, width }) {
    return (
        <TextField size="small" select fullWidth value={value ?? ''} onChange={e => onChange(e.target.value)} sx={{ minWidth: width }}>
            {options.map(o => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
            ))}
        </TextField>
    );
}

export default function TimberSalesForm({ form }) {
    const {
        state,
        setField,
        addSalesRow,
        updateSalesRow,
        removeSalesRow,
        salesOptions,
        uploadedDocs,
        setUploadedDocs,
    } = form;
    const [rowToDelete, setRowToDelete] = useState(null);
    const salesRows = state.salesRows ?? [];

    const {
        fullSPDetails,
        isLoading: isLoadingSP,
        fetchSPDetails,
    } = useGetWhDetails()

    const {
        customers,
        isLoading: isLoadingCustomers,
        fetchCustomers,
    } = useGetCustomers()

    const {
        products,
        isLoading: isLoadingProducts,
        fetchProducts,
    } = useGetProducts()

    useEffect(() => {
        fetchSPDetails()
        fetchCustomers()
        fetchProducts()
    }, [fetchSPDetails, fetchCustomers])

    const confirmDeleteRow = async () => {
        if (!rowToDelete) return;
        const removed = await removeSalesRow(rowToDelete);
        if (removed) setRowToDelete(null);
    };

    if (isLoadingSP || isLoadingCustomers || isLoadingProducts) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={200}
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* ── Customer & agreement ─────────────────────────────── */}
            <FieldGroup title="Customer & agreement">
                <Grid item xs={12} sm={6}>
                    <Autocomplete
                        size="small"
                        options={fullSPDetails}
                        value={fullSPDetails.find((o) => o.value === state.salesWhNumber) || null}
                        onChange={(e, v) => setField('salesWhNumber', v?.value || '')}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        renderInput={(p) => <TextField {...p} label="WH Number" />}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Autocomplete
                        size="small"
                        options={customers}
                        value={customers.find((o) => o.value === state.salesCustomerName) || null}
                        onChange={(e, v) => setField('salesCustomerName', v?.value || '')}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        renderInput={(p) => <TextField {...p} required label="Customer" />}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth required label="Forest Manager" value={state.salesForestManager}
                        onChange={e => setField('salesForestManager', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="Customer Email" placeholder="Customer email to confirm pricing"
                        value={state.salesCustomerEmail} onChange={e => setField('salesCustomerEmail', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="WTG Email" placeholder="WTG email to confirm pricing"
                        value={state.salesWtgEmail} onChange={e => setField('salesWtgEmail', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Typography sx={pillLabelSx}>Email Agreement Copy To Customer?</Typography>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={state.salesEmailCopyToCustomer}
                        onChange={(e, v) => v && setField('salesEmailCopyToCustomer', v)}
                        sx={pillGroupSx}
                    >
                        <ToggleButton value="Yes">Yes</ToggleButton>
                        <ToggleButton value="No">No</ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth type="date" label="Effective From" InputLabelProps={{ shrink: true }}
                        value={state.salesEffectiveFrom} onChange={e => setField('salesEffectiveFrom', e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth type="date" label="Effective To" InputLabelProps={{ shrink: true }}
                        value={state.salesEffectiveTo} onChange={e => setField('salesEffectiveTo', e.target.value)} />
                </Grid>
            </FieldGroup>

            {/* ── Price & Volume table ─────────────────────────────── */}
            <Box sx={cardSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#4a3f2f' }}>Price & Volume</Typography>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => addSalesRow()}>
                        Add row
                    </Button>
                </Box>

                {salesRows.length === 0 ? (
                    <Typography sx={{ fontSize: '0.85rem', color: '#8a8375', fontStyle: 'italic', py: 1 }}>
                        No rows yet. Add at least one to continue.
                    </Typography>
                ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 980 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}>Location</TableCell>
                                    <TableCell sx={headCellSx}>Product</TableCell>
                                    <TableCell sx={headCellSx}>Unit</TableCell>
                                    <TableCell sx={headCellSx}>EUR / UOM</TableCell>
                                    <TableCell sx={headCellSx}>Quality</TableCell>
                                    <TableCell sx={headCellSx}>Volume</TableCell>
                                    <TableCell sx={headCellSx}>Weekly Vol.</TableCell>
                                    <TableCell sx={headCellSx} align="center">Completed</TableCell>
                                    <TableCell sx={headCellSx} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {salesRows.map(row => {
                                    const set = (name, value) => updateSalesRow(row.rowId, name, value);
                                    return (
                                        <TableRow key={row.rowId}>
                                            <TableCell sx={cellSx}>
                                                <SelectCell value={row.nameLocation} options={LOCATION_OPTIONS} onChange={v => set('nameLocation', v)} width={115} />
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <Autocomplete
                                                    size="small"
                                                    options={products}
                                                    value={products.find((o) => o.value === state.productCode) || null}
                                                    onChange={(e, v) => setField('productCode', v?.value || '')}
                                                    isOptionEqualToValue={(o, v) => o.value === v.value}
                                                    renderInput={p => <TextField {...p} />}
                                                    sx={{ minWidth: 190 }}
                                                />
                                                
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <SelectCell value={row.unitOfMeasure} options={UOM_OPTIONS} onChange={v => set('unitOfMeasure', v)} width={95} />
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <TextField size="small" type="number" placeholder="0.00" disabled={!row.unitOfMeasure}
                                                    value={row.eurPerUOM} onChange={e => set('eurPerUOM', e.target.value)} sx={{ width: 100 }} />
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <SelectCell value={row.quality} options={QUALITY_OPTIONS} onChange={v => set('quality', v)} width={110} />
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <TextField size="small" type="number" placeholder="0"
                                                    value={row.vol} onChange={e => set('vol', e.target.value)} sx={{ width: 90 }} />
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <TextField size="small" type="number" placeholder="0"
                                                    value={row.weeklyVolume} onChange={e => set('weeklyVolume', e.target.value)} sx={{ width: 90 }} />
                                            </TableCell>
                                            <TableCell sx={cellSx} align="center">
                                                <Checkbox
                                                    checked={row.completed === 'Yes'}
                                                    onChange={e => set('completed', e.target.checked ? 'Yes' : 'No')}
                                                />
                                            </TableCell>
                                            <TableCell sx={cellSx} align="right">
                                                <IconButton size="small" color="error" onClick={() => setRowToDelete(row)}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {/* ── Terms & notes ────────────────────────────────────── */}
            <FieldGroup title="Terms & notes">
                <Grid item xs={12}>
                    <TextField size="small" fullWidth multiline rows={3} label="Terms & Conditions For Customer"
                        placeholder="Enter terms here…" value={state.salesTerms}
                        onChange={e => setField('salesTerms', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                    <TextField size="small" fullWidth multiline rows={3} label="Internal Notes"
                        placeholder="Enter WTG internal notes…" value={state.salesInternalNotes}
                        onChange={e => setField('salesInternalNotes', e.target.value)} />
                </Grid>
            </FieldGroup>

            {/* ── Attachments ──────────────────────────────────────── */}
            <FieldGroup title="Attachments">
                <Grid item xs={12} sx={{ minWidth: 0, overflow: 'hidden' }}>
                    <DropzoneUpload
                        label="Upload Documents"
                        multiple
                        uploadedFiles={uploadedDocs}
                        onFileSelect={files => setUploadedDocs(files)}
                        onFileDelete={file => setUploadedDocs(prev => prev.filter(f => f !== file))}
                        onFileView={() => { }}
                    />
                </Grid>
            </FieldGroup>

            <Dialog open={Boolean(rowToDelete)} onClose={() => setRowToDelete(null)}>
                <DialogTitle>Delete price & volume row?</DialogTitle>
                <DialogContent>
                    <DialogContentText>This row will be permanently removed.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRowToDelete(null)} color="inherit">Cancel</Button>
                    <Button onClick={confirmDeleteRow} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}